import { Language, LayoutType, ServiceEntityType } from '@consigli/types';

import { commonValidators, ValidationRules } from '@/utils/validation';

import { ServiceFormData } from './types';

/**
 * Validation config for the service form
 */
export const validationRules: ValidationRules<ServiceFormData> = {
  serviceType: {
    required: true,
    validate: (value: unknown): string | undefined => {
      const validServiceTypes = Object.values(ServiceEntityType);
      return commonValidators.enumValue(value, validServiceTypes, 'Service type is required');
    },
  },
  name: {
    required: true,
    minLength: 2,
    validate: (value: unknown): string | undefined => {
      return commonValidators.stringWithMinLength(
        value,
        2,
        'Service name must be at least 2 characters',
      );
    },
  },
  packageType: {
    required: false, // Will be conditionally required based on serviceType
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const serviceType = args[0] as ServiceEntityType;

      if (Object.values(ServiceEntityType).includes(serviceType)) {
        if (!value) {
          return 'Package type is required';
        }
      }

      return undefined;
    },
  },
  language: {
    required: false, // Will be conditionally required based on serviceType
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const serviceType = args[0] as ServiceEntityType;

      if (serviceType === ServiceEntityType.DOCUMENT_PACKAGE) {
        if (!value) {
          return 'Language is required for document packages';
        }
        const validLanguages = Object.values(Language);
        return commonValidators.enumValue(value, validLanguages, 'Invalid language');
      }

      return undefined;
    },
  },
  inputType: {
    required: false, // Will be conditionally required based on serviceType
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const serviceType = args[0] as ServiceEntityType;

      if (serviceType === ServiceEntityType.BUILDING_PACKAGE) {
        if (!value) {
          return 'Input type is required for building packages';
        }
        const validInputTypes = ['files', 'json'];
        return commonValidators.enumValue(value, validInputTypes, 'Invalid input type');
      }

      return undefined;
    },
  },
  comments: {
    required: false,
    validate: (value: unknown): string | undefined => {
      return commonValidators.optionalString(value);
    },
  },
  floorHeight: {
    required: false, // Will be conditionally required based on serviceType and packageType
    minValue: 0,
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const serviceType = args[0] as ServiceEntityType;
      const packageType = args[1] as LayoutType;

      if (
        serviceType === ServiceEntityType.LAYOUT_OPTIMIZATION &&
        packageType === LayoutType.PLANT_ROOM
      ) {
        if (!value || (typeof value === 'number' && value <= 0)) {
          return 'Floor height is required and must be greater than 0 for plant room layouts';
        }
      }

      if (value !== null && value !== undefined) {
        return commonValidators.positiveNumber(value, 'Floor height must be a positive number');
      }

      return undefined;
    },
  },
  files: {
    required: false, // Will be conditionally required based on serviceType and inputType
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const serviceType = args[0] as ServiceEntityType;
      const inputType = args[1] as string;

      if (serviceType === ServiceEntityType.BUILDING_PACKAGE && inputType === 'files') {
        if (!value || (Array.isArray(value) && value.length < 2)) {
          return 'At least 2 files are required for building packages with file input (Architectural model and 3D model of technical equipment)';
        }
      }

      if (serviceType === ServiceEntityType.BUILDING_PACKAGE && inputType === 'json') {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return 'A JSON file is required for building packages with JSON input';
        }
      }

      if (serviceType === ServiceEntityType.LAYOUT_OPTIMIZATION) {
        if (!value || (Array.isArray(value) && value.length < 1)) {
          return 'At least 1 file is required for layout optimization';
        }
      }

      return undefined;
    },
  },
} as const;
